import moment from 'moment';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const colourStyle = [
  {
    "textColor": "black",
    "bgColor": "rgb(123, 183, 73)"
  },
  {
    "textColor": "white",
    "bgColor": "rgb(154, 82, 23)"
  },
  {
    "textColor": "white",
    "bgColor": "rgb(0, 83, 1)"
  },
  {
    "textColor": "white",
    "bgColor": "rgb(204, 0, 114)"
  }
];

const DraggableHeader = ({ id, children, index, moveColumn, isVisible }) => { 

  const [, ref] = useDrag({
    type: 'COLUMN_HEADER',
    item: { id, index },
  });

  const [, drop] = useDrop({
    accept: 'COLUMN_HEADER',
    hover(item) {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  if (!isVisible) return null;

  return (
    <th 
      ref={(node) => ref(drop(node))} 
      style={{ cursor: 'move' }}
      className={id === 'serial' ? 'w30px' : id === 'action' || id === 'history' ? 'text-center' : ''}
    >
      {children}
    </th>
  );
};

const DataCenterTable = ({ tableData, totalRows, selectedDataCenter, setSelectedDataCenter, columnVisibility }) => {
  const navigate = useNavigate();
  const [columnsOrder, setColumnsOrder] = useState([
    { id: 'serial', label: 'S.No.', visible: columnVisibility.serial },
    { id: 'name', label: 'Data Center Name', className: 'position-relative', visible: columnVisibility.name },
    { id: 'lastModified', label: 'Last Modified', visible: columnVisibility.lastModified },
    { id: 'modifiedBy', label: 'Modified by', visible: columnVisibility.modifiedBy },
    { id: 'history', label: 'History', visible: columnVisibility.history },
    { id: 'action', label: 'Action', visible: columnVisibility.action },
  ]);

  const moveColumn = (fromIndex, toIndex) => {
    const newColumns = [...columnsOrder];
    const [movedColumn] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, movedColumn);
    setColumnsOrder(newColumns);
  };

  const handleModifyClick = (dataCenterId) => {
    navigate(`/LearningDevelopment/${dataCenterId}`);
  };

  const renderTableCell = (item, index, columnId) => {
    switch (columnId) {
      case 'serial':
        return <label className="form-check-label">{index + 1}</label>;
      case 'name':
        return (
          <div className="text-primary-900 ps-5 fw-bold">
            <span className="imgAbst bg-primary-900">
              <img src="assets/images/data-center/data-center-icon.svg" alt="Data Center" />
            </span>
            {item.name}
          </div>
        );
      case 'lastModified':
        return moment(item?.updatedTs).format('DD-MM-YYYY');
      case 'modifiedBy':
        return (
          <div className="d-flex align-items-center gap-2">
            <span
              className="userimg"
              style={{
                backgroundColor: colourStyle[index]?.bgColor,
                color: colourStyle[index]?.textColor
              }}
            >
              {item?.userId?.firstName?.charAt(0)}{item?.userId?.lastName?.charAt(0)}
            </span>
            <div className="flex-grow-1">
              <h6 className="text-md mb-0 fw-medium text-primary-600 text-start">
                {item?.userId?.firstName} {item?.userId?.lastName}
              </h6>
            </div>
          </div>
        );
      case 'history':
        return (
          <div
            className="d-flex align-items-center gap-2 justify-content-center"
            data-bs-toggle="offcanvas"
            data-bs-target="#viewHistPopup"
            aria-controls="viewHistPopup"
            onClick={() => setSelectedDataCenter(item._id)}
          >
            <svg
              width={25}
              height={16}
              viewBox="0 0 25 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5 0.846924C7.91454 0.846924 3.75621 3.35567 0.687788 7.43054C0.437404 7.76438 0.437404 8.23078 0.687788 8.56463C3.75621 12.6444 7.91454 15.1532 12.5 15.1532C17.0855 15.1532 21.2438 12.6444 24.3122 8.56954C24.5626 8.23569 24.5626 7.76929 24.3122 7.43545C21.2438 3.35567 17.0855 0.846924 12.5 0.846924ZM12.8289 13.0372C9.78506 13.2286 7.2714 10.7199 7.46287 7.6711C7.61998 5.15745 9.65741 3.12001 12.1711 2.96291C15.2149 2.77144 17.7286 5.28019 17.5371 8.32897C17.3751 10.8377 15.3377 12.8752 12.8289 13.0372ZM12.6767 10.7101C11.037 10.8132 9.68196 9.46306 9.78997 7.8233C9.87343 6.46828 10.9732 5.37347 12.3282 5.2851C13.9679 5.182 15.323 6.5321 15.2149 8.17187C15.1266 9.5318 14.0268 10.6266 12.6767 10.7101Z"
                fill="#7B7E8B"
              />
            </svg>
            <span className="text-md mb-0 fw-normal text-secondary-light">
              View
            </span>
          </div>
        );
      case 'action':
        return (
          <button
            onClick={() => handleModifyClick(item._id)}
            className="btn btn-outline-primary px-24 py-4 fw-medium text-sm"
          >
            Modify
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="table-responsive table-container" id="table-container">
        <table className="table basic-border-table mb-0 table-hover">
          <thead>
            <tr className="dnd-moved">
              {columnsOrder.map((column, index) => (
                <DraggableHeader
                  key={column.id}
                  id={column.id}
                  index={index}
                  moveColumn={moveColumn}
                  isVisible={column.visible}
                >
                  {column.label}
                </DraggableHeader>
              ))}
            </tr>
          </thead>
          <tbody id="table-body">
            {tableData.map((item, index) => (
              <tr key={item.id} className="dnd-moved">
                {columnsOrder.map((column) => (
                  column.visible && (
                    <td
                    key={`${item.id}-${column.id}`}
                    className={[
                      (column.id === 'action' || column.id === 'history' || column.id === 'modifiedBy') ? 'text-center' : '',
                      column.className || ''
                    ].filter(Boolean).join(' ')}
                  >
                    {renderTableCell(item, index, column.id)}
                  </td>
                  )
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={1} />
              <td colSpan={2} className="fw-bold">
                Total Rows
              </td>
              <td colSpan={2} />
              <td className="text-center totalRowNumber">
                <span className="d-block fw-bold">{totalRows}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </DndProvider>
  );
};

export default DataCenterTable;